import { useEffect, useCallback, useMemo } from 'react'
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios'
//


import { useAuthStore } from 'src/store/auth'
import { useUserStore } from 'src/store/user'
import { queryClient } from 'src/lib/query-client'
import { prefetchReports } from 'src/service/reports.service'

import LogRocket from 'logrocket'
import { AuthContext } from './auth-context'

import { isValidToken, setSession } from './utils'

type Props = {
  children: React.ReactNode
}

export function AuthProvider({ children }: Props) {
  const { loading, setLoading } = useAuthStore()

  const userStorage = useUserStore((state) => state.user)

  const { signed } = useAuthStore()
  const { setUser } = useUserStore()

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null)
    useAuthStore.getState().logout()
    useUserStore.getState().logout()
    queryClient.clear()
  }, [])

  const initialize = useCallback(async () => {
    try {
      const accessToken = useAuthStore.getState().token

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken)

        try {
          const { data: response } = await axios.get(API_ENDPOINTS.auth.me)
          LogRocket.identify(response._id, {
            name: response.name,
            email: response.email,
          })
          setUser(response)
          prefetchReports(queryClient, { isMaster: response?.role === 'admin' }).catch(() => undefined)
        } catch (fetchError: any) {
          const status = fetchError?.statusCode || fetchError?.response?.status || fetchError?.status
          if (status === 401 || status === 403) {
            logout()
          }
          // Erros de rede ou outros: mantem dados persistidos, nao desloga
        }
      } else if (accessToken) {
        // Token claramente expirado (ex.: passaram-se mais de 30 dias)
        logout()
      }
    } catch {
      // Fallback: mantem o estado persistido
    } finally {
      useAuthStore.getState().setLoading(false)
    }
  }, [logout, setUser])

  useEffect(() => {
    initialize()
    // Importante: NAO revalidamos a sessao no evento `online`. Quando a
    // internet volta, confiamos no estado persistido localmente. A proxima
    // requisicao normal cuidara de reportar 401 caso o token tenha expirado,
    // mas o usuario nao e deslogado por causa de oscilacoes de rede.
  }, [initialize])

  // LOGIN
  const login = useCallback(
    async (email: string, password: string, returnTo: string | null) => {
      setLoading(true)
      const data = {
        email,
        password,
      }

      const { data: response } = await axios.post(API_ENDPOINTS.auth.login, data)
      LogRocket.identify(response._id, {
        name: response.name,
        email: response.email,
      })

      axios.defaults.headers.Authorization = `Bearer ${response.token}`
      setUser(response)
      setSession(response.token)
      prefetchReports(queryClient, { isMaster: response?.role === 'admin' }).catch(() => undefined)
    },
    [setUser, setLoading],
  )

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, name: string) => {
      setLoading(true)

      const data = {
        email,
        password,
        name,
      }

      await axios.post(API_ENDPOINTS.auth.register, data)

      const dataLogin = {
        email,
        password,
      }

      const { data: response } = await axios.post(API_ENDPOINTS.auth.login, dataLogin)

      setUser(response)
      setSession(response.token)
    },
    [setUser, setLoading],
  )

  // ----------------------------------------------------------------------

  const checkAuthenticated = signed ? 'authenticated' : 'unauthenticated'

  const status = loading ? 'loading' : checkAuthenticated

  const memoizedValue = useMemo(
    () => ({
      user: userStorage,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      login,
      register,
      logout,
    }),
    [login, logout, register, userStorage, status],
  )

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>
}
